import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/pages/auth/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState(false);
  const [topError, setTopError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/app", { replace: true });
  }, [user, navigate]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid work email.";
    if (form.password.length < 6) e.password = "Password must be at least 6 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setTopError("");
    if (!validate()) return;
    setLoading(true);
    const res = await signup({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
      company: form.company.trim(),
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to Vvvoraa.");
      navigate("/app", { replace: true });
    } else {
      setTopError(res.error);
    }
  };

  return (
    <AuthShell
      title="Create your Vvvoraa account"
      subtitle="Claim your Control Center sandbox."
      footer={<>Already have an account? <Link to="/login" className="text-[#4F8CFF] hover:underline" data-testid="link-login">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form" noValidate>
        {topError && (
          <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-300" data-testid="signup-error">
            {topError}
          </div>
        )}
        <div>
          <Label htmlFor="name" className="text-slate-300">Name</Label>
          <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)}
            className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
            placeholder="Ada Lovelace" data-testid="signup-name" />
          {errors.name && <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="email" className="text-slate-300">Work Email</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
            className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
            placeholder="you@company.com" data-testid="signup-email" />
          {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="password" className="text-slate-300">Password</Label>
          <div className="relative mt-1.5">
            <Input id="password" type={show ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)}
              className="bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF] pr-10"
              placeholder="At least 6 characters" data-testid="signup-password" />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" aria-label="Toggle password visibility">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="mt-1.5 text-xs text-red-400">{errors.password}</p>}
        </div>
        <div>
          <Label htmlFor="company" className="text-slate-300">Company</Label>
          <Input id="company" value={form.company} onChange={(e) => set("company", e.target.value)}
            className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
            placeholder="Acme AI" data-testid="signup-company" />
        </div>

        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold" data-testid="signup-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
        </Button>

        <div className="relative py-1 text-center">
          <span className="relative z-10 bg-[#05080D] px-3 text-xs text-slate-500">or</span>
          <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
        </div>

        <Button
          type="button" variant="outline"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/app";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full h-11 bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full"
          data-testid="google-signup"
        >
          Continue with Google
        </Button>
      </form>
    </AuthShell>
  );
}
